import React from "react";

// 답변 본문의 [n] 각주를 클릭 가능한 위첨자 칩으로 치환한다.
// react-markdown 이 파싱한 자식(문자열/엘리먼트)을 재귀적으로 훑으며,
// 문자열 조각 안의 /\[(\d+)\]/ 패턴만 <CiteChip> 으로 바꾼다.
// (URL 링크는 이미 <a> 로 파싱되므로 각주와 충돌하지 않는다.)

interface ChipProps {
  n: number;
  valid: boolean;        // sourceMeta 범위 안의 번호인지(밖이면 일반 텍스트)
  active: boolean;
  onCite: (n: number) => void;
}

function CiteChip({ n, valid, active, onCite }: ChipProps) {
  if (!valid) return <span>[{n}]</span>; // 근거 목록에 없는 번호는 비활성 텍스트
  return (
    <sup>
      <button
        type="button"
        onClick={() => onCite(n)}
        className={
          "mx-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded px-1 text-[10px] font-semibold leading-none align-super transition-colors " +
          (active
            ? "bg-[#0E1E2D] text-white"
            : "bg-[#0E1E2D]/10 text-[#0E1E2D] hover:bg-[#0E1E2D]/20")
        }
        aria-label={`근거 ${n}번 문서 보기`}
        title={`근거 ${n}번 문서로 이동`}
      >
        {n}
      </button>
    </sup>
  );
}

export function linkifyCitations(
  children: React.ReactNode,
  opts: { maxIndex: number; active: number | null; onCite: (n: number) => void },
  keyPrefix = "c",
): React.ReactNode {
  return React.Children.map(children, (child, ci) => {
    if (typeof child === "string") {
      const re = /\[(\d{1,3})\]/g;
      const out: React.ReactNode[] = [];
      let last = 0;
      let m: RegExpExecArray | null;
      let hit = 0;
      while ((m = re.exec(child)) !== null) {
        if (m.index > last) out.push(child.slice(last, m.index));
        const n = parseInt(m[1], 10);
        out.push(
          <CiteChip
            key={`${keyPrefix}-${ci}-${hit}`}
            n={n}
            valid={n >= 1 && n <= opts.maxIndex}
            active={opts.active === n}
            onCite={opts.onCite}
          />,
        );
        last = m.index + m[0].length;
        hit += 1;
      }
      if (hit === 0) return child;
      if (last < child.length) out.push(child.slice(last));
      return out;
    }
    if (React.isValidElement(child)) {
      const el = child as React.ReactElement<{ children?: React.ReactNode }>;
      if (el.props?.children) {
        return React.cloneElement(
          el,
          el.props,
          linkifyCitations(el.props.children, opts, `${keyPrefix}-${ci}`),
        );
      }
    }
    return child;
  });
}

// BookStack 원문을 근거 문장 위치로 스크롤·강조하기 위한 텍스트프래그먼트 URL.
// (교차출처 iframe 내부 제어는 불가 → 새 탭 최상위 내비게이션에서 브라우저가 하이라이트)
export function fragmentUrl(url: string | null | undefined, quote?: string): string {
  if (!url) return "";
  const clean = (quote || "").replace(/\s+/g, " ").trim().slice(0, 60);
  if (!clean) return url;
  const enc = encodeURIComponent(clean);
  return url.includes("#") ? `${url}` : `${url}#:~:text=${enc}`;
}
